import { Box, Button, Grid, InputLabel, Stack, styled, Tab, Tabs, TextField, Typography } from '@mui/material'
import { grey, orange, teal } from '@mui/material/colors'
import { ComponentProps } from 'react'
import { CustomButtonProps, CustomPhoneContainerProps, StatusBoxProps } from '../interfaceTypes'
import palette from './palette'
import { flexCenter } from './styleConstants'

// color palette
const { lightGray, lightGrayTwo, primary } = palette

export const AuthLayoutContainer = styled(Box)(() => ({
  minHeight: '100vh',
  paddingInline: '24px',
  ...flexCenter
}))

export const AuthLayoutBox = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: '4px',
  padding: '50px',
  marginTop: '40px',
  boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 1px 3px rgba(0, 0, 0, 0.12)',

  [theme.breakpoints.down('md')]: {
    padding: '24px'
  }
}))

export const CardBox = styled(Box)(() => ({
  backgroundColor: 'white',
  borderRadius: '4px',
  boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 1px 3px rgba(0, 0, 0, 0.12)',

  '& .documentCardActive': {
    background: teal[50],
    border: `1px solid ${teal[100]}`
  }
}))

export const CardHeader = styled(Stack)(() => ({
  justifyContent: 'space-between',
  padding: '24px',
  alignItems: 'center',
  borderBottom: `1px solid ${lightGray}`
}))

export const StatusBox = styled(Typography)<StatusBoxProps>(({ borderColor }) => ({
  display: 'inline-block',
  fontWeight: '500',
  border: `1px solid ${borderColor}`,
  borderRadius: '30px',
  padding: '2px 11px'
}))

export const EmptyDataContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
}))

export const TabsContainer = styled(Tabs)(() => ({
  display: 'inline-flex',
  borderRadius: '6px',
  minHeight: '40px'
}))

export const TabItem = styled(Tab)(() => ({
  fontSize: '14px',
  padding: '10px 16px',
  minHeight: '40px'
}))

export const CustomPhoneContainer = styled(Box)<CustomPhoneContainerProps>(({ focusBorder }) => ({
  '.react-tel-input .form-control': {
    height: '56px',

    '&:focus-visible': {
      outline: focusBorder ? 'none' : `2px solid ${palette.primary.main}`
    }
  },

  '.MuiFormControl-root.MuiFormControl-fullWidth': {
    marginTop: '8px'
  },

  '.MuiInputLabel-outlined.MuiInputLabel-shrink': {
    transform: ' translate(10px, -6px) scale(0.75)',
    background: 'none',
    padding: '0 6px',
    zIndex: '99',
    backgroundColor: 'white'
  },

  '.display_none': {
    display: 'none!important'
  },

  '.special-label': {
    display: 'none!important'
  },

  '.transparent': {
    color: 'transparent'
  },

  '.appearance': {
    backgroundColor: 'white'
  }
}))

export const Event = styled(Box)(() => ({
  borderRadius: 20,
  fontSize: 13,
  fontWeight: 400,
  marginRight: 16,
  padding: '5px 10px',
  textAlign: 'center',
  height: 32
}))

export const Search = styled(Box)(() => ({
  position: 'relative',
  width: '250px',
  marginLeft: 'auto'
}))

export const SearchIconWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}))

export const Container = styled(Grid)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: 20,
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  marginBottom: 35,
  background: 'white',
  border: '1px dashed rgba(0, 0, 0, 0.12)',
  borderRadius: 4,

  '& a': {
    fontWeight: 400,
    fontSize: 16,
    letterSpacing: 0.15,
    color: '#771117',
    paddingBottom: 2,
    borderBottom: '1px solid #771117'
  },

  '& .uploadButton': {
    background: 'rgba(119, 17, 23, 0.12)'
  }
}))

export const CustomTabButton = styled(({ isLeftItem, isCenterItem, ...rest }: CustomButtonProps & ComponentProps<typeof Button>) => (
  <Button {...rest} />
))(({ isLeftItem, isCenterItem, theme }) => ({
  '&.MuiTab-Custom': {
    fontSize: 14,
    fontWeight: 500,
    color: 'lightGrayOne',
    border: '1px solid #D0D5DD',
    borderTopLeftRadius: !isCenterItem && isLeftItem ? '8px' : 0,
    borderBottomLeftRadius: !isCenterItem && isLeftItem ? '8px' : 0,
    borderBottomRightRadius: isLeftItem || isCenterItem ? 0 : '8px',
    borderTopRightRadius: isLeftItem || isCenterItem ? 0 : '8px'
  },

  '&.Mui-selected': {
    color: '#fff',
    backgroundColor: primary.main
  },

  [theme.breakpoints.down('sm')]: {
    padding: '10px'
  }
}))

export const CaseDetailCardItem = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  borderBottom: `1px solid ${lightGrayTwo}`,
  paddingInline: '24px',
  paddingBlock: '16px'
}))

export const ErrorBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: '100%',
  transform: `translate(-50%, -50%)`,

  p: {
    [theme.breakpoints.down('sm')]: {
      padding: '10px'
    }
  }
}))

export const ErrorText = styled(Typography)(({ theme }) => ({
  fontSize: '30rem',
  fontWeight: '600',
  opacity: '0.1',

  [theme.breakpoints.down('lg')]: {
    fontSize: '25rem'
  },

  [theme.breakpoints.down('md')]: {
    fontSize: '18rem'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '10rem'
  }
}))

export const PageNotFountText = styled(Typography)(({ theme }) => ({
  fontSize: '68px',
  textAlign: 'center',

  [theme.breakpoints.down('md')]: {
    fontSize: '48px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px'
  }
}))

export const BlurBox = styled(Box)(() => ({
  backgroundColor: '#ffffffab',
  zIndex: 5,
  minHeight: '700px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  top: '0',
  left: '0',
  width: '100%'
}))

export const OutlookSignInButton = styled(Button)(() => ({
  textTransform: 'unset',
  borderColor: '#0078d4',
  color: '#0078d4',
  padding: '5px 24px',
  fontWeight: '500',

  '&:hover': {
    color: '#0078d4',
    borderColor: '#0078d4'
  }
}))

export const EventsLoader = styled(Box)(() => ({
  height: '100%',
  width: '100%',
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  top: '0',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  borderRadius: 4
}))

export const CardBoxTopBar = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '24px',
  borderBottom: '1px solid',
  borderColor: lightGrayTwo
}))

export const DocumentBox = styled(Box)(() => ({
  boxShadow: `0px 0px 0px 1px ${grey[300]}`,
  borderRadius: '4px',
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap'
}))

export const FilterOuterWrapper = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  padding: '15px',
  flexWrap: 'wrap',
  gap: '10px',
  position: 'relative'
}))

export const UploadCsvTopBar = styled(Box)(() => ({
  bgcolor: orange[500],
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}))

export const PersonalSettingFormBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid',
  borderColor: lightGrayTwo
}))

export const SearchWithSortBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '20px'
}))

export const RightChatBox = styled(Box)(() => ({
  marginLeft: 'auto',
  marginBottom: 20,
  width: '70%',
  border: '1px solid #771117',
  padding: 20,
  borderRadius: '20px 20px 20px 0'
}))

export const LeftChatBox = styled(Box)(() => ({
  marginBottom: 20,
  maxWidth: '460px',
  width: '100%',
  border: '1px solid #771117',
  padding: 20,
  borderRadius: '20px 20px 0 20px'
}))

export const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
  fontSize: '16px',
  color: '#0009'
}))

export const BillPreviewSummarySection = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    justifyContent: 'space-between'
  },

  '& *': { fontSize: '12px !important' },

  '@media print': {
    display: 'flex !important',
    justifyContent: 'space-between !important',
    gap: '20px !important'
  }
}))

export const BillPreviewTotalAmountBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#F9F9FA',
  borderRadius: 1,
  border: '1px solid #DFE4EA',
  padding: theme.spacing(1.2),

  [theme.breakpoints.up('md')]: {
    alignSelf: 'flex-start',
    marginLeft: 'auto',
    minWidth: '250px'
  },

  '@media print': {
    backgroundColor: '#F9F9FA !important',
    border: '1px solid #DFE4EA !important',
    borderRadius: '4px !important',
    padding: '10px !important',
    marginLeft: 'auto !important',
    minWidth: '250px !important',
    breakInside: 'avoid',
    pageBreakInside: 'avoid'
  }
}))

export const BillPreviewRetainerInfoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: theme.spacing(2),

  [theme.breakpoints.up('md')]: {
    paddingRight: '20%'
  },

  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(3)
  }
}))

export const StyledAmountInput = styled(TextField)(({ theme }) => ({
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0
  },
  '& input[type=number]': {
    MozAppearance: 'textfield'
  }
}))

export const FieldBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 20,
  paddingLeft: 4,
  paddingTop: 10
}))
